// Microsoft variant of the Duplicate Conversion-Action Names evidence renderer. The shape
// differs from Google's (no per-action categories, no 30d volume); we show MS's
// ExcludeFromBidding flag and the categorical TrackingStatus in its place.

import type { EvidenceProps } from './registry';

interface GoalRow {
  id?: string;
  name?: string;
  type?: string;
  scope?: string;
  status?: string;
  excludeFromBidding: boolean;
  trackingStatus?: string;
}

interface DuplicateGroup {
  normalizedName: string;
  displayName: string;
  count: number;
  contributingCount: number;
  members: GoalRow[];
}

interface DuplicateMsShape {
  totalGoals: number;
  duplicateGroupCount: number;
  groups: DuplicateGroup[];
}

function formatTracking(t: string | undefined): string {
  if (!t) return '—';
  if (t === 'RecordingConversions') return 'Firing';
  if (t === 'NoRecentConversions') return 'Dormant';
  if (t === 'NotTracking') return 'Not tracking';
  return t;
}

export const DuplicateConversionGoalsMicrosoftEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as DuplicateMsShape;
  const hasDupes = e.duplicateGroupCount > 0;

  if (!hasDupes) {
    return (
      <div className='evidence'>
        <p className='evidence__lead'>
          All {e.totalGoals} Active/Paused conversion goals have unique names. No duplicates found.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        <strong>{e.duplicateGroupCount}</strong> duplicate name group
        {e.duplicateGroupCount === 1 ? '' : 's'} found across {e.totalGoals} conversion goals.
        Microsoft uses a flat goal model — when ≥ 2 same-named goals both contribute to bidding (
        <code>ExcludeFromBidding=false</code>), they split the Conversions signal under the same
        label.
      </p>

      {e.groups.map((g, i) => (
        <section key={g.normalizedName} className='evidence__section'>
          <h4 className='evidence__section-title'>
            <span className='evidence__group-name'>&ldquo;{g.displayName}&rdquo;</span>
            <span className='evidence__section-tag evidence__section-tag--context'>
              {g.count} goals · {g.contributingCount} contributing
            </span>
            {g.contributingCount >= 2 && (
              <span className='evidence__section-tag evidence__section-tag--actionable'>
                Bidding signal split
              </span>
            )}
          </h4>
          <table className='evidence__table evidence__table--duplicate-ms'>
            <thead>
              <tr>
                <th>Goal name</th>
                <th>Type</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Role</th>
                <th>Tracking</th>
              </tr>
            </thead>
            <tbody>
              {g.members.map(m => (
                <tr key={m.id ?? `${m.name}-${i}`}>
                  <td>
                    <div>{m.name ?? '(unnamed)'}</div>
                    {m.id && <div className='evidence__action-id'>{m.id}</div>}
                  </td>
                  <td>{m.type ?? '—'}</td>
                  <td>{m.scope ?? '—'}</td>
                  <td>{m.status ?? '—'}</td>
                  <td>
                    <span
                      className={`evidence__pill ${
                        m.excludeFromBidding
                          ? 'evidence__pill--excluded'
                          : 'evidence__pill--in-metric'
                      }`}
                    >
                      {m.excludeFromBidding ? 'Excluded' : 'Contributing'}
                    </span>
                  </td>
                  <td>{formatTracking(m.trackingStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
};
